import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, Heart, MapPin, RefreshCw, Sparkles, Ticket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import EventPreviewModal, { EventTicketTier } from './EventPreviewModal';
import { CartItem, useCart } from '../contexts/CartContext';
import { ClassData, getClasses, getFullImageUrl } from '../services/api';
import { getCurrentToken, getCurrentUser } from '../utils/userStorage';
import {
  applyCheckoutUpdatesToCart,
  CheckoutAuthRequiredError,
  CheckoutRevalidationError,
  createMercadoPagoPreference,
  redirectToCheckoutAuth,
  summarizeCheckoutChanges
} from '../utils/checkoutFlow';
import {
  getPeekPassDate,
  getPeekPassOrganizer,
  getPeekPassSummary,
  getPeekPassTitle,
  getPeekPassVenue,
  mapCategoryToEvent,
  mapDifficultyToExperience,
} from '../utils/peekPassBrand';
import { buildPeekPassEventFeed, sortPeekPassEvents } from '../utils/eventFeed';

const NewsPage: React.FC = () => {
  const navigate = useNavigate();
  const { addToCart, replaceCartItems } = useCart();
  const [events, setEvents] = useState<ClassData[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<ClassData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadNews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getClasses({ limit: 100, page: 1 });
      setEvents(buildPeekPassEventFeed(response.data || []).filter((event) => event.isActive !== false));
    } catch (requestError: any) {
      setEvents(buildPeekPassEventFeed([]));
      setError(`${requestError.message || 'No pudimos cargar las tendencias.'}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadNews(); }, [loadNews]);

  const featuredEvents = useMemo(() => {
    const featured = events.filter((event) => event.isPopular);
    return sortPeekPassEvents(featured.length ? featured : events).slice(0, 3);
  }, [events]);

  const latestEvents = useMemo(() => {
    const featuredIds = new Set(featuredEvents.map(event => String(event._id)));
    return sortPeekPassEvents(events).filter(event => !featuredIds.has(String(event._id)));
  }, [events, featuredEvents]);

  const buildEventCartItem = (event: ClassData, ticketTier?: EventTicketTier): CartItem => {
    const discount = ticketTier ? 0 : (typeof event.discount === 'number' ? event.discount : 0);
    const basePrice = ticketTier?.price ?? event.price;
    return {
      id: ticketTier ? `${event._id || event.title}:${ticketTier.id}` : (event._id || event.title),
      title: getPeekPassTitle(event),
      description: getPeekPassSummary(event),
      price: discount ? basePrice * (1 - discount / 100) : basePrice,
      quantity: 1,
      originalPrice: basePrice,
      discountPercent: discount,
      currency: event.currency || 'ARS',
      category: mapCategoryToEvent(event.category),
      difficulty: mapDifficultyToExperience(event.difficulty),
      duration: Number.parseInt(event.duration, 10) || 0,
      instructor: getPeekPassOrganizer(event.organizer?.name),
      image: event.thumbnail,
      videoUrl: event.reelUrl,
      benefits: event.benefits || [],
      requirements: event.requirements || [],
      exercises: event.exercises || [],
      eventDate: getPeekPassDate(event),
      venue: getPeekPassVenue(event),
      ticketTierName: ticketTier?.name,
      eventId: event._id,
      ticketTierId: ticketTier?.id,
      serviceFeeRate: event.serviceFeeRate || 5,
      organizerId: event.organizer?.id,
      courseId: event._id,
      canReceivePayments: event.organizer?.canReceivePayments
    };
  };

  const addEventItemToCart = (item: CartItem) => {
    const result = addToCart(item);
    if (result.success) return true;

    if (window.confirm(`${result.error}\n\n¿Querés vaciar el carrito y continuar con este evento?`)) {
      replaceCartItems([item]);
      return true;
    }

    return false;
  };

  const reserveEvent = (event: ClassData, ticketTier?: EventTicketTier) => {
    const item = buildEventCartItem(event, ticketTier);
    if (!addEventItemToCart(item)) return;
    setSelectedEvent(null);
    navigate('/cart');
  };

  const checkoutEvent = async (event: ClassData, ticketTier?: EventTicketTier) => {
    if (!event._id || event.isFallback || event._id.startsWith('mock-')) {
      throw new Error('El pago directo está disponible cuando el organizador publica el evento.');
    }

    const item = buildEventCartItem(event, ticketTier);

    if (!getCurrentUser() || !getCurrentToken()) {
      if (!addEventItemToCart(item)) {
        throw new Error('La compra no se inició porque el carrito actual pertenece a otro organizador.');
      }

      setSelectedEvent(null);
      redirectToCheckoutAuth({
        type: 'direct-checkout',
        returnTo: '/cart?checkout=1',
        eventId: event._id,
        ticketTierId: ticketTier?.id
      });
      return;
    }

    try {
      const result = await createMercadoPagoPreference([item], {
        externalReferencePrefix: `peekpass_event_${event._id}`,
        organizerId: item.organizerId || null,
        courseId: event._id
      });
      const paymentUrl = result.payment_url || result.init_point;
      if (!paymentUrl) throw new Error('Mercado Pago no devolvió una URL de pago.');
      window.location.href = paymentUrl;
    } catch (error: any) {
      if (error instanceof CheckoutAuthRequiredError) {
        if (!addEventItemToCart(item)) return;
        setSelectedEvent(null);
        redirectToCheckoutAuth({
          type: 'direct-checkout',
          returnTo: '/cart?checkout=1',
          eventId: event._id,
          ticketTierId: ticketTier?.id
        });
        return;
      }

      if (error instanceof CheckoutRevalidationError) {
        const updatedItems = applyCheckoutUpdatesToCart([item], error.result);
        replaceCartItems(updatedItems);
        const changes = summarizeCheckoutChanges(error.result);

        if (updatedItems.length && window.confirm(`${changes}\n\nActualizamos tu carrito. ¿Querés confirmar los cambios y continuar con Mercado Pago?`)) {
          const result = await createMercadoPagoPreference(updatedItems, {
            acceptChanges: true,
            externalReferencePrefix: `peekpass_event_${event._id}`,
            organizerId: updatedItems[0]?.organizerId || null,
            courseId: event._id
          });
          const paymentUrl = result.payment_url || result.init_point;
          if (!paymentUrl) throw new Error('Mercado Pago no devolvió una URL de pago.');
          window.location.href = paymentUrl;
          return;
        }

        throw new Error(updatedItems.length ? 'Revisá el carrito actualizado antes de continuar.' : changes);
      }

      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f5ef] text-slate-950">
      <Header showBackButton onBack={() => navigate('/')} title="Tendencias" />
      <main className="w-full px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <section className="mx-auto max-w-7xl">
          <p className="text-xs font-bold uppercase tracking-[0.26em] text-[#71931b]">PEEK PASS. presenta</p>
          <div className="mt-3 flex flex-col gap-5 border-b border-slate-200 pb-8 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="max-w-3xl text-4xl font-black tracking-[-0.07em] sm:text-5xl">Las tendencias que mueven la agenda.</h1>
              <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">Los eventos publicados más recientes, con artistas, preventas y experiencias para descubrir.</p>
            </div>
            <button type="button" onClick={loadNews} className="inline-flex w-fit items-center gap-2 border-b-2 border-black pb-2 text-sm font-black text-black transition hover:border-[#71931b] hover:text-[#71931b]">
              <RefreshCw className="h-4 w-4" /> Actualizar
            </button>
          </div>

          {loading ? (
            <div aria-label="Cargando tendencias" className="mt-9 grid animate-pulse gap-5 sm:grid-cols-2 lg:grid-cols-3"><div className="h-[360px] rounded-2xl border border-slate-200 bg-white" /><div className="h-[360px] rounded-2xl border border-slate-200 bg-white" /><div className="h-[360px] rounded-2xl border border-slate-200 bg-white" /></div>
          ) : (
            <>
              {error ? (
                <div className="mt-8 border-l-4 border-black bg-white p-5 text-sm text-slate-700 shadow-sm">
                  {error}
                  <button type="button" onClick={loadNews} className="ml-4 inline-flex items-center gap-2 border-b border-black pb-1 text-xs font-black text-black transition hover:border-[#71931b] hover:text-[#71931b]"><RefreshCw className="h-3.5 w-3.5" /> Reintentar</button>
                </div>
              ) : null}
              <section className="mt-9">
                <div className="mb-5 flex items-center gap-3"><Sparkles className="h-5 w-5 text-[#71931b]" /><h2 className="text-2xl font-black tracking-[-0.05em]">Tendencias destacadas</h2></div>
                {featuredEvents.length ? (
                  <div className="grid gap-5 lg:grid-cols-3">
                    {featuredEvents.map((event) => <FeaturedCard key={event._id || event.title} event={event} onClick={() => setSelectedEvent(event)} />)}
                  </div>
                ) : <EmptyNews />}
              </section>

              <section className="mt-14">
                <div className="mb-5 flex items-center gap-3"><Ticket className="h-5 w-5 text-[#71931b]" /><h2 className="text-2xl font-black tracking-[-0.05em]">Eventos publicados recientemente</h2></div>
                <div className="grid gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {latestEvents.map((event) => <NewsCard key={event._id || event.title} event={event} onClick={() => setSelectedEvent(event)} />)}
                </div>
              </section>
            </>
          )}
        </section>
      </main>
      {selectedEvent ? <EventPreviewModal event={selectedEvent} onClose={() => setSelectedEvent(null)} onAddToCart={reserveEvent} onCheckout={checkoutEvent} onRequireLogin={() => navigate('/')} /> : null}
    </div>
  );
};

const getDateBadge = (event: ClassData) => {
  const parsed = new Date(event.eventDate || event.createdAt);
  const date = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  const parts = new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: 'short' }).formatToParts(date);
  const day = parts.find((part) => part.type === 'day')?.value || '--';
  const month = (parts.find((part) => part.type === 'month')?.value || '').replace('.', '').toUpperCase();
  return { day, month: month || 'MES' };
};

const FeaturedCard: React.FC<{ event: ClassData; onClick: () => void }> = ({ event, onClick }) => {
  const dateBadge = getDateBadge(event);
  return <button type="button" onClick={onClick} className="group overflow-hidden border border-slate-200 bg-white text-left shadow-[0_20px_45px_-35px_rgba(15,23,42,0.5)] transition hover:-translate-y-1 hover:border-[#a8ca42]">
    <div className="relative h-64 overflow-hidden"><img src={getFullImageUrl(event.thumbnail)} alt={getPeekPassTitle(event)} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" /><div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" /><span className="absolute left-4 top-4 bg-[#dff38c] px-3 py-1 text-xs font-black text-black">{mapCategoryToEvent(event.category)}</span><span className={`absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold shadow-sm ${event.likes?.length ? 'border-rose-200 bg-rose-50 text-rose-600' : 'border-white/20 bg-black/65 text-white'}`}><Heart className={`h-3.5 w-3.5 ${event.likes?.length ? 'fill-current' : ''}`} />{event.likes?.length || 0}</span><p className="absolute inset-x-5 bottom-5 text-2xl font-black tracking-[-0.05em] text-white">{getPeekPassTitle(event)}</p></div>
    <div className="grid grid-cols-[62px_1fr] gap-3 p-5"><div className="border-r border-slate-200 pr-3 text-center"><strong className="block text-2xl leading-none">{dateBadge.day}</strong><span className="mt-1 block text-[10px] font-black uppercase tracking-[0.12em] text-[#71931b]">{dateBadge.month}</span><span className="mt-1 block text-[9px] font-semibold text-slate-400">{new Date(event.eventDate || event.createdAt).getFullYear()}</span></div><div><p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><MapPin className="h-4 w-4 text-[#71931b]" />{getPeekPassVenue(event)}</p><p className="mt-2 line-clamp-2 text-sm leading-5 text-slate-600">{getPeekPassSummary(event)}</p></div></div>
  </button>;
};

const NewsCard: React.FC<{ event: ClassData; onClick: () => void }> = ({ event, onClick }) => (
  <button type="button" onClick={onClick} className="group text-left">
    <div className="relative aspect-[4/3] overflow-hidden bg-slate-200"><img src={getFullImageUrl(event.thumbnail)} alt={getPeekPassTitle(event)} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" /><span className="absolute left-3 top-3 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-950">{mapCategoryToEvent(event.category)}</span><span className={`absolute right-3 top-3 inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-bold shadow-sm ${event.likes?.length ? 'border-rose-200 bg-rose-50 text-rose-600' : 'border-white/20 bg-black/65 text-white'}`}><Heart className={`h-3 w-3 ${event.likes?.length ? 'fill-current' : ''}`} />{event.likes?.length || 0}</span></div>
    <div className="mt-3"><p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.13em] text-[#71931b]"><CalendarDays className="h-3.5 w-3.5" />{getPeekPassDate(event)}</p><h3 className="mt-2 text-lg font-black tracking-[-0.04em] text-slate-950 transition group-hover:text-[#536d12]">{getPeekPassTitle(event)}</h3><p className="mt-1 text-sm text-slate-600">{getPeekPassOrganizer(event.organizer?.name)}</p></div>
  </button>
);

const EmptyNews = () => <div className="border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-600">Todavía no hay tendencias publicadas. Volvé pronto para descubrir las próximas fechas.</div>;

export default NewsPage;
